/**
 * Converts a given size (number or string) into a numeric value.
 *
 * @param {number | string} receivedSize - The size to be converted. It can be a number or a string.
 * If it's a string, it may include the 'px' suffix, which will be removed during conversion.
 *
 * @returns {number | undefined} - Returns the numeric value of the size if conversion is successful.
 * If the input is invalid or cannot be converted, it returns `undefined`.
 *
 * @description
 * This function takes a size input, which can either be a number or a string. If the input is a string
 * and contains the 'px' suffix, the suffix is removed before converting the value to a number. If the
 * input is null, undefined, or cannot be converted to a number, the function returns `undefined`.
 *
 * @example
 * // Example usage:
 * const size1 = convertSizeToNumber('16px'); // Returns: 16
 * const size2 = convertSizeToNumber(20);     // Returns: 20
 * const size3 = convertSizeToNumber('abc');  // Returns: undefined
 * const size4 = convertSizeToNumber(null);   // Returns: undefined
 */
export const convertSizeToNumber = (receivedSize: number | string): number | undefined => {
  return (
    parseFloat(`${receivedSize ?? ''}`) ||
    Number(`${receivedSize ?? ''}`.replace('px', '')) ||
    undefined
  );
};
